/**
 * openReconnectingSocket — den ene gjenoppkoblingssløyfen bak alle /ws-hookene.
 *
 * Åpner en WebSocket mot `/ws`, sender `subscribe` for de oppgitte emnene ved
 * hver 'open', og kobler til igjen etter 'close' med eksponentiell backoff
 * (1s/2s/4s …, maks 30s — HIGH-5).
 *
 * Backoff nullstilles først når socketen har holdt seg åpen i STABLE_OPEN_MS,
 * ikke på 'open'. Huben godtar oppgraderingen og lukker deretter med 4401 i
 * onOpen når økten mangler (T-ws-01), så en utlogget fane ser 'open' rett før
 * hver avvisning. Da hookene nullstilte på 'open' vokste backoff aldri forbi
 * 1s, og ti hooker i samme fane ga en `ws-close`-linje i API-loggen hvert
 * ~14. ms i det uendelige. Første servermelding duger ikke som signal heller:
 * huben sender ingenting til en innlogget klient som ikke har noe å melde.
 *
 * 4401 får vanlig backoff i stedet for å stoppe sløyfen: kjøkkentavla og en
 * fane som logges inn igjen skal fortsatt plukke opp socketen av seg selv,
 * nå med inntil 30 s forsinkelse.
 *
 * Returnerer en dispose-funksjon som avbryter planlagt gjenoppkobling og
 * lukker socketen — returner den rett fra useEffect.
 */
import type { TopicName } from '@stell/shared';

export const MAX_BACKOFF_MS = 30_000;

/** Hvor lenge en socket må holde seg åpen før backoff regnes som frisk igjen. */
export const STABLE_OPEN_MS = 5_000;

/** Ventetid før forsøk nr. `attempts` (1-basert): 1s, 2s, 4s … maks 30s. */
export function backoffMs(attempts: number): number {
  return Math.min(MAX_BACKOFF_MS, 1_000 * Math.pow(2, attempts - 1));
}

export interface ReconnectingSocketOpts {
  topics: readonly TopicName[];
  onMessage: (ev: MessageEvent) => void;
}

export function openReconnectingSocket(opts: ReconnectingSocketOpts): () => void {
  let ws: WebSocket | null = null;
  let attempts = 0;
  let cancelled = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let stableTimer: ReturnType<typeof setTimeout> | null = null;

  const connect = (): void => {
    if (cancelled) return;
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const sock = new WebSocket(`${proto}://${window.location.host}/ws`);
    ws = sock;
    sock.addEventListener('open', () => {
      stableTimer = setTimeout(() => {
        stableTimer = null;
        attempts = 0;
      }, STABLE_OPEN_MS);
      // Huben abonnerer alle innloggede klienter på 'system' automatisk;
      // de øvrige emnene er opt-in.
      try {
        sock.send(JSON.stringify({ kind: 'subscribe', topics: opts.topics }));
      } catch {
        // Socketen kan allerede være CLOSING — 'close' planlegger neste forsøk.
      }
    });
    sock.addEventListener('message', (ev) => opts.onMessage(ev as MessageEvent));
    sock.addEventListener('close', () => {
      if (stableTimer) {
        clearTimeout(stableTimer);
        stableTimer = null;
      }
      if (cancelled) return;
      attempts += 1;
      reconnectTimer = setTimeout(connect, backoffMs(attempts));
    });
  };

  connect();
  return () => {
    cancelled = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    if (stableTimer) clearTimeout(stableTimer);
    ws?.close();
  };
}
